'use client'

import { ChangeEvent, MouseEvent, ReactNode, RefObject, useRef } from 'react'
import { Button, Cell, Text } from '@vinted/web-ui'
import { noop } from 'lodash'

import { useTranslate } from '@marketplace-web/shared/i18n'

type Props = {
  name: string
  acceptedFileTypes?: string
  title?: string
  value?: File | null
  validation?: ReactNode
  disabled?: boolean
  styling?: ComponentProps<typeof Cell>['styling']
  translationPrefix?: string
  isInfoIconVisible: boolean
  maximumFileSize: number
  fileInputRef?: RefObject<HTMLInputElement | null>
  inputInformationLink?: {
    information_link: (chunks: Array<ReactNode>) => JSX.Element
  }
  onChange?: (file: File | null, message?: string) => void
  onButtonClick?: ((currentButtonState: 'upload' | 'delete') => void) | null
}

const MAX_FILENAME_LENGTH = 40

const FileInput = ({
  name,
  acceptedFileTypes,
  value,
  title,
  validation,
  styling,
  disabled,
  translationPrefix,
  isInfoIconVisible,
  maximumFileSize,
  fileInputRef,
  inputInformationLink,
  onChange = noop,
  onButtonClick = null,
}: Props) => {
  const baseTranslate = useTranslate()
  const translate = useTranslate(translationPrefix)

  const mainFileInputRef = useRef<HTMLInputElement>(null)
  const fileInput = fileInputRef || mainFileInputRef

  function onFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files?.length) return

    const file = event.target.files[0]!

    if (file.size > maximumFileSize) {
      const maxFileSizeInMegabytes = maximumFileSize / 1024 / 1024

      onChange(file, baseTranslate('errors.max_file_size', { size: maxFileSizeInMegabytes }))

      return
    }

    onChange(file)
  }

  function onFileInputClick(event: MouseEvent) {
    const element = event.target as HTMLInputElement

    element.value = ''
  }

  function handleClick() {
    if (onButtonClick) {
      onButtonClick(value ? 'delete' : 'upload')

      return
    }

    if (value) {
      onChange(null)
    } else {
      fileInput.current?.click()
    }
  }

  function renderTitle() {
    return (
      <div className="u-flexbox u-flex-direction-column">
        <span>{title || translate('title')}</span>
        {isInfoIconVisible && !value && (
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('what_it_is', inputInformationLink)}
          />
        )}
      </div>
    )
  }

  function renderBody() {
    if (!value) return null

    return (
      <Text
        as="span"
        type={Text.Type.Body}
        width={Text.Width.Parent}
        truncate={value.name.length > MAX_FILENAME_LENGTH}
        text={value.name}
      />
    )
  }

  return (
    <div className="file-input">
      <Cell
        styling={styling}
        disabled={disabled}
        title={renderTitle()}
        suffix={
          <Button
            text={translate(`buttons.${value ? 'delete' : 'upload'}`)}
            size={Button.Size.Small}
            theme={value ? 'warning' : 'primary'}
            onClick={handleClick}
          />
        }
        body={renderBody()}
        validation={validation}
      />
      <input
        name={name}
        ref={fileInput}
        onChange={onFileInputChange}
        onClick={onFileInputClick}
        hidden
        type="file"
        accept={acceptedFileTypes}
      />
    </div>
  )
}

export default FileInput
